import React from "react";
import { Table } from "@mui/material";
import { useIsMobileShell } from "../../common/navLayout";
import { TS_COLORS } from "./TroubleshootingUi";

/**
 * A Troubleshooter table that stacks on phones (ENG-2060). On desktop it is
 * the same `<Table size="small">` as before. On small screens every row
 * becomes a card and every cell a "Label: value" line, the label read from
 * the table's own header cell in that column, so no table needs a second,
 * mobile-only layout. Rows that span columns (an expanded check, an
 * "affected sites" list) keep their full width.
 */
export const ResponsiveTable = ({
  children,
  minWidth,
  sx,
  testId,
}: {
  children: React.ReactNode;
  /** Desktop minimum width; the card around it scrolls sideways below it. */
  minWidth?: number;
  sx?: Record<string, unknown>;
  testId?: string;
}) => {
  const mobile = useIsMobileShell();
  const ref = React.useRef<HTMLTableElement | null>(null);

  // Label each body cell from its column's header text. Runs after every
  // render because rows come and go; a data attribute is all it touches.
  React.useLayoutEffect(() => {
    const table = ref.current;
    if (!mobile || !table) return;
    const headers = Array.from(
      table.querySelectorAll("thead tr:first-child th")
    ).map((th) => (th.textContent || "").trim());
    table.querySelectorAll("tbody tr").forEach((row) => {
      let column = 0;
      Array.from(row.children).forEach((cell) => {
        const span = Number((cell as HTMLTableCellElement).colSpan) || 1;
        cell.setAttribute("data-label", span > 1 ? "" : headers[column] || "");
        column += span;
      });
    });
  });

  if (!mobile)
    return (
      <Table
        ref={ref}
        size="small"
        data-testid={testId}
        sx={{ ...(minWidth ? { minWidth } : {}), ...sx }}
      >
        {children}
      </Table>
    );

  return (
    <Table
      ref={ref}
      size="small"
      data-testid={testId}
      data-stacked="true"
      sx={{
        display: "block",
        "& thead": { display: "none" },
        "& tbody": { display: "block" },
        "& tbody tr": {
          display: "block",
          border: `1px solid ${TS_COLORS.line}`,
          borderRadius: "6px",
          mx: 1.5,
          my: 1,
          py: 0.75,
          backgroundColor: "#fff",
        },
        "& tbody td": {
          display: "flex",
          gap: 1,
          alignItems: "baseline",
          justifyContent: "space-between",
          flexWrap: "wrap",
          border: 0,
          px: 1.5,
          py: 0.5,
          textAlign: "right",
          wordBreak: "break-word",
        },
        "& tbody td[data-label]:not([data-label=''])::before": {
          content: "attr(data-label)",
          fontWeight: 700,
          color: TS_COLORS.ink3,
          textAlign: "left",
          flexShrink: 0,
        },
        "& tbody td[data-label='']": {
          display: "block",
          textAlign: "left",
        },
        ...sx,
      }}
    >
      {children}
    </Table>
  );
};
